// src/components/PrintPreviewModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import imgSrc from '../utils/imgSrc';
import PerformanceCharts from './PerformanceCharts'; // <-- added

/**
 * PrintPreviewModal
 *
 * Props:
 *  - show: boolean
 *  - onHide: fn
 *  - selectedFan: fan object
 *  - formData: form data object (airFlow, staticPressure, airTemperature, frequency, ...)
 *  - nearestPoint: nearest performance point object
 *  - motorsForCategory: array of motor candidates
 *  - selectedMotorForPrint: currently selected motor object (auto-chosen)
 *  - setSelectedMotorForPrint: setter to change selected motor
 *  - chartData: chart.js data for performance curve (optional)
 *  - powerData: chart.js data for power chart (optional)
 *  - userPoint: the user's input point (optional)
 *
 * This component renders a modal preview and exposes a Print button which opens a new window
 * with printable HTML (the same layout used before) and triggers window.print().
 */

export default function PrintPreviewModal({
  show,
  onHide,
  selectedFan,
  formData = {},
  nearestPoint,
  motorsForCategory = [],
  selectedMotorForPrint,
  setSelectedMotorForPrint,
  chartData = null,    // new prop
  powerData = null,    // new prop
  userPoint = null,    // new prop
  certificates = [],
  showPowerChart = true,
}) {
  // Resolve API-hosted storage paths to absolute URLs (e.g., storage/fans/x.jpg)
  const toPublicUrl = (path) => {
    if (!path) return '';
    if (typeof path !== 'string') return '';
    const p = path.trim();
    let url = '';
    if (/^https?:\/\//i.test(p)) {
      // already absolute - replace domain for images
      url = p;
    } else {
      // Prefer configured API base; otherwise infer from window location; fallback to localhost:8000
      const inferredBase = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : 'http://localhost:8000';
      const apiBaseRaw = process.env.REACT_APP_API_BASE_URL || inferredBase;
      const apiBase = apiBaseRaw.replace(/\/$/, '');
      const laravelBase = apiBase.replace(/\/api$/i, '');
      if (p.startsWith('public/')) url = `${laravelBase}/storage/${p.slice('public/'.length)}`;
      else if (p.startsWith('storage/')) url = `${laravelBase}/${p}`;
      else if (p.startsWith('/storage/')) url = `${laravelBase}${p}`;
      else url = `${laravelBase}/storage/${p}`; // paths saved on public disk like "fans/abc.jpg"
    }
    // Replace api.apt.com.co with apt.com.co for images
    return url.replace(/api\.apt\.com\.co/gi, 'apt.com.co');
  };
  // Escape HTML to avoid injection when writing to print window
  const escapeHtml = (s) => String(s ?? '').replace(/[&<>"'`=\/]/g, (c) => {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;',
      '`': '&#x60;',
      '=': '&#x3D;',
    }[c];
  });

  // Helper to get airflow unit label
  const getAirflowUnitLabel = (unit) => {
    switch (unit) {
      case 'm3h': return 'm³/h';
      case 'm3s': return 'm³/s';
      case 'ls': return 'l/s';
      case 'cfm': return 'cfm';
      default: return 'm³/h';
    }
  };

  // Helper to get pressure unit label
  const getPressureUnitLabel = (unit) => {
    switch (unit) {
      case 'pa': return 'Pa';
      case 'mmh2o': return 'mm H₂O';
      case 'inchh2o': return 'inch H₂O';
      case 'mbar': return 'mbar';
      case 'kpa': return 'kPa';
      default: return 'Pa';
    }
  };

  // Helper to safely convert to number
  const toNumberSafe = (v, fallback = null) => {
    if (v === null || v === undefined || v === '') return fallback;
    if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
    const n = Number(String(v).trim());
    return Number.isFinite(n) ? n : fallback;
  };

  // Convert airflow from m³/h (database unit) to selected unit
  const convertAirflowFromM3H = (value, unit) => {
    const numValue = toNumberSafe(value, null);
    if (numValue === null) return null;

    switch (unit) {
      case 'm3h':
        return numValue; // already in m³/h
      case 'm3s':
        return numValue / 3600; // m³/h to m³/s: divide by 3600
      case 'ls':
        return numValue / 3.6; // m³/h to l/s: divide by 3.6
      case 'cfm':
        return numValue / 1.69901079552; // m³/h to cfm: divide by 1.69901079552
      default:
        return numValue; // fallback to no conversion
    }
  };

  // Convert pressure from Pa (database unit) to selected unit
  const convertPressureFromPa = (value, unit) => {
    const numValue = toNumberSafe(value, null);
    if (numValue === null) return null;

    switch (unit) {
      case 'pa':
        return numValue; // already in Pa
      case 'mmh2o':
        return numValue / 9.80665; // Pa to mm H₂O: divide by 9.80665
      case 'inchh2o':
        return numValue / 249.08891; // Pa to inch H₂O: divide by 249.08891
      case 'mbar':
        return numValue / 100; // Pa to mbar: divide by 100
      case 'kpa':
        return numValue / 1000; // Pa to kPa: divide by 1000
      default:
        return numValue; // fallback to no conversion
    }
  };

  const convertPressureToInchH2O = (valuePa) => convertPressureFromPa(valuePa, 'inchh2o');

  const computeDerived = () => {
    // Get unit labels for display
    const airFlowUnitLabel = getAirflowUnitLabel(formData.airFlowUnit || 'm3h');
    const pressureUnitLabel = getPressureUnitLabel(formData.staticPressureUnit || 'pa');
    
    const ambientTempUser = Number(formData.airTemperature ?? 20);
    const airFlowUser = parseFloat(formData.airFlow ?? 0);
    const staticPressureUser = parseFloat(formData.staticPressure ?? 0);
  
    // Use workingPoint (intersection point) passed as nearestPoint prop
    // These values are in database units (m³/h and Pa)
    const airFlowWorkingRaw = Number(
      nearestPoint?.airFlow ??
      nearestPoint?.airflow_m3h ??
      nearestPoint?.air_flow ??
      0
    );
    const staticPressureWorkingRaw = Number(
      nearestPoint?.staticPressure ??
      nearestPoint?.static_pressure ??
      nearestPoint?.static_pressure_pa ??
      0
    );

    // Convert to user's selected units for display
    const airFlowWorking = convertAirflowFromM3H(airFlowWorkingRaw, formData.airFlowUnit || 'm3h') ?? 0;
    const staticPressureWorking = convertPressureFromPa(staticPressureWorkingRaw, formData.staticPressureUnit || 'pa') ?? 0;

    // Keep raw values in database units for calculations (dynamic pressure, etc.)
    const airFlowWorkingM3H = airFlowWorkingRaw;
    const staticPressureWorkingPa = staticPressureWorkingRaw;
  
    // Get shape from product group (fan_type)
    // Try multiple possible paths to get fan shape
    const fanShape = selectedFan?.fan_type?.shape 
      || selectedFan?.fanType?.shape 
      || selectedFan?.fan_category?.fan_type?.shape
      || selectedFan?.fanCategory?.fanType?.shape
      || (selectedFan?.diameter > 0 ? 'circle' : 'square'); // Default to circle if diameter exists, otherwise square
    
    // DB values: diameter (mm) for circle, width/height (mm) for square
    const rawDiameterMm = Number(selectedFan?.diameter ?? 0);
    const rawWidthMm = Number(selectedFan?.width ?? 0);
    const rawHeightMm = Number(selectedFan?.height ?? 0);
    const tempK = ambientTempUser + 273.15;

    let dynamicPressure = '-';
    let dynamicPressureInUserUnit = null;
    let totalDynamicPressure = '-';
    let airExitVelocity = '-';
    let totalEfficiency = '-';
    let staticEfficiency = '-';

    // Calculate area based on shape
    let area = 0;
    // Try circle first if diameter exists (even if shape is not explicitly 'circle')
    // Use raw value in m³/h for calculations
    if ((fanShape === 'circle' || rawDiameterMm > 0) && rawDiameterMm > 0 && airFlowWorkingM3H > 0) {
      try {
        // Convert diameter (mm) -> meters, then calculate radius
        const diameterMeters = rawDiameterMm / 1000.0; // mm -> m
        const radiusMeters = diameterMeters / 2.0; // radius = diameter / 2

        if (!(radiusMeters > 0)) throw new Error('invalid diameter');

        area = Math.PI * Math.pow(radiusMeters, 2); // m^2 (circle: π * r²)
      } catch (err) {
        area = 0;
      }
    } else if (fanShape === 'square' && rawWidthMm > 0 && rawHeightMm > 0 && airFlowWorkingM3H > 0) {
      try {
        // Convert width and height (mm) -> meters
        const widthMeters = rawWidthMm / 1000.0; // mm -> m
        const heightMeters = rawHeightMm / 1000.0; // mm -> m

        if (!(widthMeters > 0) || !(heightMeters > 0)) throw new Error('invalid width or height');

        area = widthMeters * heightMeters; // m^2 (square: width * height)
      } catch (err) {
        area = 0;
      }
    }

    if (area > 0 && airFlowWorkingM3H > 0) {
      try {
  
        // Convert volumetric flow from m^3/h -> m^3/s
        const q_m3_s = Number(airFlowWorkingM3H) / 3600.0;
  
        // Exit velocity (m/s)
        const v = q_m3_s / area;
  
        // Air density using ideal gas (Pa / (R * T)) with standard atmospheric pressure
        const rho = 101325.0 / (287.058 * tempK); // kg/m^3
  
        // Correct dynamic pressure formula: 0.5 * rho * V^2
        const dpVal = 0.5 * rho * v * v;
  
        const dpValPa = Number.isFinite(dpVal) ? dpVal : null;
        dynamicPressure = dpValPa !== null ? dpValPa.toFixed(2) : '-';
        // Convert dynamic pressure to user's unit for total pressure calculation and display
        dynamicPressureInUserUnit = dpValPa !== null 
          ? convertPressureFromPa(dpValPa, formData.staticPressureUnit || 'pa') 
          : null;
        const totalPa = (Number.isFinite(staticPressureWorkingPa) ? staticPressureWorkingPa : 0) + (dpValPa ?? 0);
        const totalInUserUnit =
          dynamicPressure !== '-' && !Number.isNaN(staticPressureWorking) && dynamicPressureInUserUnit !== null
            ? (dynamicPressureInUserUnit + Number(staticPressureWorking))
            : null;
        totalDynamicPressure = Number.isFinite(totalInUserUnit) ? totalInUserUnit.toFixed(2) : '-';
        // Calculate total efficiency using cfm, inch H2O, and shaft power (hp)
        const airFlowCfm = convertAirflowFromM3H(airFlowWorkingM3H, 'cfm');
        const totalPressureInch = convertPressureToInchH2O(totalPa);
        const staticPressureInch = convertPressureToInchH2O(staticPressureWorkingPa);
        const shaftPowerNearestRaw = nearestPoint
          ? (nearestPoint.shaftPower ?? nearestPoint.shaft_power ?? nearestPoint.power ?? nearestPoint.shaft_power_kw ?? nearestPoint.shaftPowerKw ?? null)
          : null;
        const shaftPowerNearest = shaftPowerNearestRaw !== null && shaftPowerNearestRaw !== undefined
          ? Number(shaftPowerNearestRaw)
          : null;
        const shaftPowerHp = shaftPowerNearest !== null && Number.isFinite(shaftPowerNearest)
          ? shaftPowerNearest / 0.7457
          : null;
        if (Number.isFinite(airFlowCfm) && Number.isFinite(totalPressureInch) && Number.isFinite(shaftPowerHp) && shaftPowerHp > 0) {
          const eff = (airFlowCfm * totalPressureInch) / (6343 * shaftPowerHp);
          totalEfficiency = Number.isFinite(eff) ? eff.toFixed(4) : '-';
        }
        if (Number.isFinite(airFlowCfm) && Number.isFinite(staticPressureInch) && Number.isFinite(shaftPowerHp) && shaftPowerHp > 0) {
          const effStatic = (airFlowCfm * staticPressureInch) / (6343 * shaftPowerHp);
          staticEfficiency = Number.isFinite(effStatic) ? effStatic.toFixed(4) : '-';
        }
  
        airExitVelocity = Number.isFinite(v) ? v.toFixed(2) : '-';
      } catch (err) {
        dynamicPressure = '-';
        totalDynamicPressure = '-';
        airExitVelocity = '-';
      }
    } else if (area === 0) {
      // No valid dimensions for area calculation
      dynamicPressure = '-';
      dynamicPressureInUserUnit = null;
      totalDynamicPressure = '-';
      airExitVelocity = '-';
    } else {
      if (!Number.isNaN(staticPressureWorking) && staticPressureWorking !== 0) {
        totalDynamicPressure = staticPressureWorking.toFixed(2);
      }
    }
  
    const shaftPowerNearestRaw = nearestPoint
      ? (nearestPoint.shaftPower ?? nearestPoint.shaft_power ?? nearestPoint.power ?? nearestPoint.shaft_power_kw ?? nearestPoint.shaftPowerKw ?? null)
      : null;
    const shaftPowerNearest = shaftPowerNearestRaw !== null && shaftPowerNearestRaw !== undefined
      ? Number(shaftPowerNearestRaw)
      : null;

    // NOTE: user requested flow rate unit to be L/s and specific fan power (SPF) in W.
    // Convert airFlowWorkingM3H (in m^3/h) -> liters per second (L/s): 1 m^3/h = 1000 L / 3600 s = 1/3.6 L/s
    const flowRate = airFlowWorkingM3H > 0 ? (Number(airFlowWorkingM3H) * 1000.0 / 3600.0) : null; // L/s

    // Convert shaft power to W before dividing. Commonly shaftPowerNearest is in kW in DB, so multiply by 1000.
    // specificFanPower (SPF) will be in W per (L/s) according to the user's instruction.
    const specificFanPower = (shaftPowerNearest !== null && flowRate !== null && flowRate > 0)
      ? ((Number(shaftPowerNearest) * 1000.0) / flowRate)
      : null;
  
    // Get pole count from motor (try multiple field names)
    const motorPoleCount = selectedMotorForPrint 
      ? Number(selectedMotorForPrint.no_of_poles ?? selectedMotorForPrint.noOfPoles ?? selectedMotorForPrint.no_of_pole ?? 0) 
      : 0;
    
    // Try to use motor's nominal fan speed first, then calculate from poles and frequency
    let workingRPM = null;
    
    if (selectedMotorForPrint?.nominal_fan_speed) {
      // Use nominal fan speed directly if available
      const nominalRPM = Number(selectedMotorForPrint.nominal_fan_speed);
      if (!Number.isNaN(nominalRPM) && nominalRPM > 0) {
        workingRPM = Math.round(nominalRPM);
      }
    }
    
    // If no nominal speed, calculate from poles and frequency
    if (workingRPM === null && motorPoleCount > 0) {
      // Use motor frequency if available, otherwise use formData frequency, default to 60 Hz
      const motorFrequency = selectedMotorForPrint?.frequency ?? selectedMotorForPrint?.frequency_hz ?? null;
      const freqForCalc = motorFrequency !== null ? Number(motorFrequency) : Number(formData.frequency ?? 60);
      
      // Calculate RPM: RPM = (120 * frequency) / number_of_poles
      if (freqForCalc > 0) {
        workingRPM = Math.round((120 * freqForCalc) / motorPoleCount);
      }
    }
  
    return {
      ambientTempUser,
      airFlowUser,
      airFlowUserUnit: airFlowUnitLabel,
      staticPressureUser,
      staticPressureUserUnit: pressureUnitLabel,
      airFlowWorking,
      staticPressureWorking,
      airFlowWorkingUnit: airFlowUnitLabel,
      staticPressureWorkingUnit: pressureUnitLabel,
      diameter: rawDiameterMm,          // return diameter in mm for display
      dynamicPressure: dynamicPressureInUserUnit !== null && dynamicPressureInUserUnit !== undefined 
        ? dynamicPressureInUserUnit.toFixed(2) 
        : dynamicPressure,
      totalDynamicPressure,
      airExitVelocity,
      shaftPowerNearest,
      flowRate,
      specificFanPower,
      workingRPM,
      staticEfficiency,
      totalEfficiency,
    };
  };
  

  const {
    ambientTempUser,
    airFlowUser,
    airFlowUserUnit,
    staticPressureUser,
    staticPressureUserUnit,
    airFlowWorking,
    staticPressureWorking,
    airFlowWorkingUnit,
    staticPressureWorkingUnit,
    diameter,
    dynamicPressure,
    totalDynamicPressure,
    airExitVelocity,
    shaftPowerNearest,
    flowRate,
    specificFanPower,
    workingRPM,
    staticEfficiency,
    totalEfficiency,
  } = computeDerived();

  // Build printable HTML (same as your previous generator but contained here)
  const generatePrintHtml = (chartImageDataUrl = null, powerChartImageDataUrl = null, logoImageDataUrl = null) => {
    const title = escapeHtml(selectedFan?.model_name ?? selectedFan?.model ?? selectedFan?.name ?? 'Fan');
    const brand = escapeHtml(selectedFan?.brand ?? '');
    const price = escapeHtml(String(selectedFan?.price ?? '-'));
    const fallbackProduct = imgSrc(selectedFan);
    const airFlowUnitLabel = getAirflowUnitLabel(formData.airFlowUnit || 'm3h');
    const pressureUnitLabel = getPressureUnitLabel(formData.staticPressureUnit || 'pa');

    const motorRows = selectedMotorForPrint
      ? `
        <tr><th style="width:40%">Nominal Motor Power</th><td>${escapeHtml(selectedMotorForPrint.nominal_motor_power ?? selectedMotorForPrint.nominalMotorPower ?? selectedMotorForPrint.power_kw ?? '-') } kW</td></tr>
        <tr><th>Nominal Fan Speed</th><td>${escapeHtml(selectedMotorForPrint.nominal_fan_speed ?? selectedMotorForPrint.nominalFanSpeed ?? selectedMotorForPrint.rpm ?? '-')}</td></tr>
        <tr><th>No. of Poles</th><td>${escapeHtml(selectedMotorForPrint.no_of_poles ?? selectedMotorForPrint.noOfPoles ?? '-')}</td></tr>
        <tr><th>Nominal Motor Current</th><td>${escapeHtml(selectedMotorForPrint.nominal_motor_current ?? selectedMotorForPrint.nominalMotorCurrent ?? '-') } A</td></tr>
        <tr><th>Voltage</th><td>${escapeHtml(selectedMotorForPrint.voltage ?? selectedMotorForPrint.voltage_v ?? '-')}</td></tr>
        <tr><th>Frequency</th><td>${escapeHtml(selectedMotorForPrint.frequency ?? selectedMotorForPrint.freq ?? '-') } Hz</td></tr>
        <tr><th>Phase</th><td>${escapeHtml(selectedMotorForPrint.phase ?? selectedMotorForPrint.phases ?? '-')}</td></tr>
        <tr><th>İnsulation class</th><td>${escapeHtml(selectedMotorForPrint.insulation_class ?? selectedMotorForPrint.insulationClass ?? '-')}</td></tr>
        <tr><th>Efficiency</th><td>${escapeHtml(selectedMotorForPrint.efficiency ?? selectedMotorForPrint.eff ?? '-') } %</td></tr>
        <tr><th>Working Class</th><td>${escapeHtml(selectedMotorForPrint.working_class ?? selectedMotorForPrint.workingClass ?? '-')}</td></tr>
      `
      : `<tr><td colspan="2">No suitable motor found.</td></tr>`;

    // Build images section HTML from DB fields
    // Use raw HTML from backend for rich descriptions (e.g., tables)
    const descHtml = `<div class="rich-content">${selectedFan?.description ?? ''}</div>`;
    // Normalize images: API may return array or JSON string
    let gallery = [];
    if (Array.isArray(selectedFan?.images)) {
      gallery = selectedFan.images;
    } else if (typeof selectedFan?.images === 'string') {
      try { const parsed = JSON.parse(selectedFan.images); if (Array.isArray(parsed)) gallery = parsed; } catch (_) {}
    }
    const galleryHtml = gallery.length
      ? `<div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:8px;">${gallery.map((g)=>`<img src="${escapeHtml(toPublicUrl(g))}" alt="img" style="width:120px; height:80px; object-fit:contain; border:1px solid #eee;" />`).join('')}</div>`
      : '<div style="color:#777;">No gallery images</div>';
    const spl = selectedFan?.sound_power_level_image ? `<img src="${escapeHtml(toPublicUrl(selectedFan.sound_power_level_image))}" alt="spl" style="width:160px; height:110px; object-fit:contain; border:1px solid #eee;" />` : '<span>-</span>';
    const dim = selectedFan?.dimension_image ? `<img src="${escapeHtml(toPublicUrl(selectedFan.dimension_image))}" alt="dimension" style="width:160px; height:110px; object-fit:contain; border:1px solid #eee;" />` : '<span>-</span>';
    const conn = selectedFan?.connection_diagram_image ? `<img src="${escapeHtml(toPublicUrl(selectedFan.connection_diagram_image))}" alt="connection" style="width:160px; height:110px; object-fit:contain; border:1px solid #eee;" />` : '<span>-</span>';

    // Choose top product image: prefer first gallery image, otherwise fallback
    // Ensure fallback also resolves via API host when it's a relative path
    const fallbackResolved = (/^https?:\/\//i.test(fallbackProduct) || fallbackProduct.startsWith('/'))
      ? fallbackProduct
      : toPublicUrl(fallbackProduct);
    const topSrc = gallery && gallery.length > 0 && gallery[0]
      ? toPublicUrl(gallery[0])
      : fallbackResolved;
    const productTopImg = escapeHtml(topSrc);
    // Use logo data URL if available, otherwise fallback to absolute URL
    const siteLogo = logoImageDataUrl 
      ? escapeHtml(logoImageDataUrl)
      : (() => {
          // Use the same logo path resolution as in App.js
          const siteLogoBase = (process.env.PUBLIC_URL || (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '').replace(/\/$/, '');
          return escapeHtml(`${siteLogoBase}/logo.png`.replace(/\/{2,}/g, '/'));
        })();

    // Build certificate icons row
    const certIcons = (Array.isArray(certificates) ? certificates : [])
      .map((c) => c && c.image ? `<img src="${escapeHtml(c.image)}" alt="${escapeHtml(c.name || 'certificate')}" />` : '')
      .join('');

    const headerSpecs = ``;

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title} — Print</title>
    <style>
      /* Page background: match preview modal body */
      html,body { 
        height:100%; 
        margin:0; 
        background:#f3f6f8;
        color:#0f172a; 
        font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; 
      }
      /* White paper card styled like an admin card */
      .paper {
        width: 820px;
        margin: 24px auto;
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 14px;
        box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 2px 8px rgba(16, 24, 40, 0.06);
        padding: 22px;
        box-sizing: border-box;
      }
      .header {
        display:flex;
        justify-content:space-between;
        align-items:flex-start;
        margin-bottom: 6px;
        gap: 12px;
        width: 100%;
        flex-wrap: nowrap;
      }
      .left-logo { 
        flex-shrink: 0; 
        width: 190px; 
        align-self: flex-start;
      }
      .left-logo img { 
        height:125px; 
        width:190px;
        object-fit:contain; 
        border:none; 
        padding:0; 
        background:transparent; 
      }
      .right-logo { 
        flex-shrink: 0; 
        width: 240px; 
        align-self: flex-start;
        margin-left: auto;
      }
      .right-logo img { 
        max-height:180px; 
        max-width:240px;
        object-fit:contain; 
        opacity:0.95; 
        border:none; 
        width: 100%;
      }
      .center-title { 
        text-align:center; 
        flex: 1; 
        min-width: 0;
        margin:0 18px; 
        word-wrap: break-word;
        overflow-wrap: break-word;
      }
      .center-title h1 { font-size:21px; margin:0; font-weight:700; letter-spacing:0.4px; line-height:1.3; word-wrap: break-word; color:#a82a2e; }
      .center-title .meta { font-size:12px; color:#666; margin-top:6px; }

      .intro-desc {
        margin-top:4px;
        font-size:13px;
        color:#333;
        line-height:1.4;
      }
      .intro-desc * { font-size:inherit !important; }
      .hero-description .section-title {
        margin-top:0;
      }

      /* Section headings: match the preview card exactly */
      .section-title {
        background: #a82a2e;
        background-color: #a82a2e;
        color: #ffffff;
        padding: 8px 10px;
        font-weight: 700;
        margin-top: 14px;
        border: 1px solid #e0e9f5;
        border-left: 4px solid #474747;
        font-size: 13px;
        border-radius: 10px;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      /* Certificate images should not be cropped */
      .certificates-container {
        display:flex;
        align-items:flex-start;
        flex-wrap:wrap;
        gap:12px;
        padding:8px 0;
        overflow:visible !important;
      }
      .certificates-container img {
        max-height:250px;
        max-width:100%;
        width:auto;
        height:auto;
        object-fit:contain;
        display:block;
        page-break-inside:avoid;
      }

      table { width:100%; border-collapse: collapse; margin-top:10px; font-size:13px; color:#0f172a; }
      th, td { padding:10px 8px; border-bottom:1px solid #e5e7eb; vertical-align:top; text-align:left; }
      th { color:#111827; font-weight:600; width:40%; background: #f9fafb; }
      tbody tr:hover td { background: transparent; }

      .info-small { font-size:12px; color:#666; margin-top:6px; }

      /* Rich description content (from TinyMCE) */
      .rich-content { color:#0f172a; }
      .rich-content p { margin: 6px 0; }
      .rich-content table { width:100%; border-collapse: collapse; }
      .rich-content th, .rich-content td { border:1px solid #e4e7ea; padding:6px 8px; background:#ffffff; }
      .rich-content th { background:#fbfdff; font-weight:600; }

      /* Chart box — white with light border and faint grid look */
      .chart-placeholder {
        width:100%;
        height:360px;
        border:1px solid #e5e7eb;
        display:flex;
        align-items:center;
        justify-content:center;
        color:#666;
        background:
          linear-gradient(transparent 24px, rgba(229, 231, 235, 0.55) 25px),
          linear-gradient(90deg, transparent 24px, rgba(229, 231, 235, 0.55) 25px);
        background-size: 100% 25px, 25px 100%;
        margin-top:10px;
      }

      .footer-row { 
        margin-top:18px; 
        display:flex; 
        justify-content:space-between; 
        align-items:center; 
        color:#6b7280; 
        font-size:12px; 
      }
      .no-print { display:inline-block; }

      @media print {
        html,body { background: #fff; }
        .paper { box-shadow:none; margin:0; border:none; width:auto; padding:8px; border-radius:0; }
        .no-print { display:none; }
      }
    </style>
  </head>
  <body>
    <div class="paper">
      <div class="header">
        <div class="left-logo"><img src="${productTopImg}" alt="product" /></div>
        <div class="center-title">
          <h1>${title}</h1>
          ${selectedFan?.description ? `<div class="intro-desc">${descHtml}</div>` : ''}
        </div>
        <div class="right-logo"><img src="${siteLogo}" alt="Site logo" /></div>
      </div>

      <div class="section-title">Design Point</div>
      <table>
        <tbody>
          <tr><th>Flow</th><td>${airFlowUser !== null && airFlowUser !== undefined ? escapeHtml(Number(airFlowUser).toFixed(2)) + ' ' + escapeHtml(airFlowUnitLabel) : '-'}</td></tr>
          <tr><th>Pressure</th><td>${staticPressureUser !== null && staticPressureUser !== undefined ? escapeHtml(Number(staticPressureUser).toFixed(2)) + ' ' + escapeHtml(pressureUnitLabel) : '-'}</td></tr>
          <tr><th>Ambient Temperature</th><td>${escapeHtml(Number(ambientTempUser).toFixed(2))} °C</td></tr>
        </tbody>
      </table>

      <div class="section-title">Duty Point</div>
      <table>
        <tbody>
          <tr><th>Flow</th><td>${airFlowWorking > 0 ? escapeHtml(Number(airFlowWorking).toFixed(2)) + ' ' + escapeHtml(airFlowWorkingUnit) : '-'}</td></tr>
          <tr><th>Static Pressure</th><td>${staticPressureWorking > 0 ? escapeHtml(Number(staticPressureWorking).toFixed(2)) + ' ' + escapeHtml(staticPressureWorkingUnit) : '-'}</td></tr>
          <tr><th>Dynamic Pressure</th><td>${dynamicPressure !== '-' ? escapeHtml(Number(dynamicPressure).toFixed(2)) + ' ' + escapeHtml(staticPressureWorkingUnit) : '-'}</td></tr>
          <tr><th>Total Pressure</th><td>${totalDynamicPressure !== '-' ? escapeHtml(Number(totalDynamicPressure).toFixed(2)) + ' ' + escapeHtml(staticPressureWorkingUnit) : '-'}</td></tr>
          <tr><th>Diameter</th><td>${diameter && !Number.isNaN(diameter) ? escapeHtml(Number(diameter).toFixed(2)) + ' mm' : '-'}</td></tr>
          <tr><th>Air Exit Velocity</th><td>${airExitVelocity !== '-' ? escapeHtml(Number(airExitVelocity).toFixed(2) + ' m/s') : '-'}</td></tr>
          <tr><th>Working Point RPM</th><td>${workingRPM !== null ? escapeHtml(Number(workingRPM).toFixed(2)) + ' rpm' : '-'}</td></tr>
          <tr><th>Shaft Power</th><td>${shaftPowerNearest !== null && !Number.isNaN(shaftPowerNearest) ? escapeHtml(Number(shaftPowerNearest).toFixed(2)) + ' kW' : '-'}</td></tr>
          <tr><th>Flow Rate (airFlow / 2.11)</th><td>${flowRate !== null && !Number.isNaN(flowRate) ? escapeHtml(Number(flowRate).toFixed(2)) : '-'}</td></tr>
          <tr><th>Specific Fan Power (SPF)</th><td>${specificFanPower !== null && !Number.isNaN(specificFanPower) ? escapeHtml(Number(specificFanPower).toFixed(2)) + ' W/(L/s)' : '-'}</td></tr>
          <tr><th>Static Efficiency</th><td>${staticEfficiency !== '-' ? escapeHtml(String(staticEfficiency)) : '-'}</td></tr>
          <tr><th>Total Efficiency</th><td>${totalEfficiency !== '-' ? escapeHtml(String(totalEfficiency)) : '-'}</td></tr>
        </tbody>
      </table>

      <div class="section-title">Fan Properties</div>
      <table>
        <tbody>
          <tr><th>Product Group</th><td>${selectedFan?.fan_type?.name ?? selectedFan?.fanType?.name ?? '-'}</td></tr>
          <tr><th>Diameter</th><td>${diameter && !Number.isNaN(diameter) && diameter > 0 ? escapeHtml(Number(diameter).toFixed(2)) + ' mm' : '-'}</td></tr>
          <tr><th>Heat Resistance</th><td>${selectedFan?.heat_resistance ? escapeHtml(Number(selectedFan.heat_resistance).toFixed(2)) + ' °C' : '-'}</td></tr>
          <tr><th>Blade Material</th><td>${selectedFan?.blade_material ? escapeHtml(String(selectedFan.blade_material)) : '-'}</td></tr>
          <tr><th>Body Material</th><td>${selectedFan?.body_material ? escapeHtml(String(selectedFan.body_material)) : '-'}</td></tr>
          <tr><th>Electrical Connection</th><td>${selectedFan?.electrical_connection ? escapeHtml(String(selectedFan.electrical_connection)) : '-'}</td></tr>
          <tr><th>Fan Drive Mode</th><td>${selectedFan?.fan_drive_mode ? escapeHtml(String(selectedFan.fan_drive_mode)) : '-'}</td></tr>
          <tr><th>Max Operating Temperature</th><td>${selectedFan?.max_operating_temperature ? escapeHtml(Number(selectedFan.max_operating_temperature).toFixed(2)) : '-'}</td></tr>
          <tr><th>Blade Number / Angle</th><td>${selectedFan?.blade_number_angle ? escapeHtml(String(selectedFan.blade_number_angle)) : '-'}</td></tr>
          <tr><th>Blow Position</th><td>${selectedFan?.blow_position ? escapeHtml(String(selectedFan.blow_position)) : '-'}</td></tr>
        </tbody>
      </table>

      <div class="section-title">Images & Diagrams</div>
      <div style="margin-top:10px;">
        ${chartImageDataUrl 
          ? `<div style="margin:8px 0;"><img src="${escapeHtml(chartImageDataUrl)}" alt="Performance Chart" style="width:100%; height:auto; border:1px solid #e4e7ea; max-height:500px; object-fit:contain;" /></div>`
          : `<div class="chart-placeholder">Performance curve chart (preview)</div>`
        }
        ${showPowerChart && powerChartImageDataUrl 
          ? `<div style="margin:16px 0 8px 0;"><h6 style="text-align:center; margin-bottom:8px; font-size:14px; font-weight:600;">Shaft Power vs Air Flow</h6><img src="${escapeHtml(powerChartImageDataUrl)}" alt="Shaft Power Chart" style="width:100%; height:auto; border:1px solid #e4e7ea; max-height:500px; object-fit:contain;" /></div>`
          : powerData && showPowerChart ? `<div class="chart-placeholder" style="margin-top:16px;">Shaft Power curve chart (preview)</div>` : ''
        }
        ${selectedFan?.sound_power_level_image ? `<div class="section-title" style="margin-top:14px;">Sound Power Level</div><div style="margin:8px 0;"><div><img src="${escapeHtml(toPublicUrl(selectedFan.sound_power_level_image))}" style="width:100%; height:auto; border:1px solid #eee;" /></div></div>` : ''}
        ${selectedFan?.dimension_image ? `<div class="section-title" style="margin-top:14px;">Dimensions</div><div style="margin:8px 0;"><div><img src="${escapeHtml(toPublicUrl(selectedFan.dimension_image))}" style="width:100%; height:auto; border:1px solid #eee;" /></div></div>` : ''}
        ${selectedFan?.connection_diagram_image ? `<div class="section-title" style="margin-top:14px;">Connection Diagram</div><div style="margin:8px 0;"><div><img src="${escapeHtml(toPublicUrl(selectedFan.connection_diagram_image))}" style="width:100%; height:auto; border:1px solid #eee;" /></div></div>` : ''}
      </div>

      <div class="section-title">Motor Information</div>
      ${selectedMotorForPrint?.image ? `<div style="margin:8px 0; text-align:center;"><img src="${escapeHtml(toPublicUrl(selectedMotorForPrint.image))}" alt="Motor Image" style="max-width:300px; max-height:300px; object-fit:contain; border:none;" /></div>` : ''}
      <table>
        <tbody>
          ${motorRows}
        </tbody>
      </table>

      ${certIcons ? `<div class="section-title">Certificates</div><div class="certificates-container">${certIcons}</div>` : ''}

      <div class="footer-row">
        <div>Generated: ${new Date().toLocaleString()}</div>
        <div class="no-print"><button onclick="window.print();" style="padding:8px 12px; border-radius:4px; border:1px solid #cfdbe9; background:#f7fbff">Print</button></div>
      </div>
    </div>
  </body>
</html>
`;
  }; // end generatePrintHtml

  const printSelectedFan = async () => {
    if (!selectedFan) {
      // eslint-disable-next-line no-alert
      alert('No fan selected to print.');
      return;
    }

    // Try to capture both charts as images from the preview modal
    let chartImageDataUrl = null;
    let powerChartImageDataUrl = null;
    let logoImageDataUrl = null;
    
    try {
      // Wait a bit for the charts to be fully rendered
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Look for the chart canvases in the modal
      const modal = document.querySelector('.modal.show');
      if (modal) {
        const chartCanvases = modal.querySelectorAll('canvas');
        if (chartCanvases.length > 0) {
          // First canvas is the performance chart
          const perfCanvas = chartCanvases[0];
          if (perfCanvas && perfCanvas.width > 0 && perfCanvas.height > 0) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = perfCanvas.width;
            tempCanvas.height = perfCanvas.height;
            const ctx = tempCanvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            ctx.drawImage(perfCanvas, 0, 0);
            chartImageDataUrl = tempCanvas.toDataURL('image/png', 1.0);
          }
          
          // Second canvas is the power chart (if exists and allowed)
          if (chartCanvases.length > 1 && powerData && showPowerChart) {
            const powerCanvas = chartCanvases[1];
            if (powerCanvas && powerCanvas.width > 0 && powerCanvas.height > 0) {
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = powerCanvas.width;
              tempCanvas.height = powerCanvas.height;
              const ctx = tempCanvas.getContext('2d');
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
              ctx.drawImage(powerCanvas, 0, 0);
              powerChartImageDataUrl = tempCanvas.toDataURL('image/png', 1.0);
            }
          }
        }
        
        // Try to capture the logo as a data URL from modal
        let logoImg = modal.querySelector('img[alt="Site logo"]');
        
        // If not found in modal, try to find it in the main page
        if (!logoImg) {
          logoImg = document.querySelector('img[alt="APT | Air Purification Trading logo"], img[alt*="logo" i]');
        }
        
        if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
          try {
            const logoCanvas = document.createElement('canvas');
            logoCanvas.width = logoImg.naturalWidth;
            logoCanvas.height = logoImg.naturalHeight;
            const logoCtx = logoCanvas.getContext('2d');
            logoCtx.drawImage(logoImg, 0, 0);
            logoImageDataUrl = logoCanvas.toDataURL('image/png', 1.0);
          } catch (logoErr) {
            // eslint-disable-next-line no-console
            console.warn('Could not capture logo image:', logoErr);
          }
        } else if (logoImg && !logoImg.complete) {
          // Wait for logo to load
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Logo load timeout')), 2000);
            logoImg.onload = () => {
              clearTimeout(timeout);
              try {
                const logoCanvas = document.createElement('canvas');
                logoCanvas.width = logoImg.naturalWidth;
                logoCanvas.height = logoImg.naturalHeight;
                const logoCtx = logoCanvas.getContext('2d');
                logoCtx.drawImage(logoImg, 0, 0);
                logoImageDataUrl = logoCanvas.toDataURL('image/png', 1.0);
                resolve();
              } catch (err) {
                reject(err);
              }
            };
            logoImg.onerror = () => {
              clearTimeout(timeout);
              reject(new Error('Logo failed to load'));
            };
          }).catch(() => {
            // Logo loading failed, will use URL fallback
          });
        } else {
          // Try to load logo directly from URL and convert to data URL
          try {
            const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
            const logoUrl = base ? `${base}/logo.png` : '/logo.png';
            const img = new Image();
            img.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => reject(new Error('Logo load timeout')), 3000);
              img.onload = () => {
                clearTimeout(timeout);
                try {
                  const logoCanvas = document.createElement('canvas');
                  logoCanvas.width = img.naturalWidth;
                  logoCanvas.height = img.naturalHeight;
                  const logoCtx = logoCanvas.getContext('2d');
                  logoCtx.drawImage(img, 0, 0);
                  logoImageDataUrl = logoCanvas.toDataURL('image/png', 1.0);
                  resolve();
                } catch (err) {
                  reject(err);
                }
              };
              img.onerror = () => {
                clearTimeout(timeout);
                reject(new Error('Logo failed to load'));
              };
              img.src = logoUrl;
            });
          } catch (loadErr) {
            // eslint-disable-next-line no-console
            console.warn('Could not load logo from URL:', loadErr);
            // Will use URL fallback
          }
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Could not capture chart image:', err);
    }

    const html = generatePrintHtml(chartImageDataUrl, powerChartImageDataUrl, logoImageDataUrl);
    const w = window.open('', '_blank', 'width=900,height=1000,scrollbars=yes');
    if (!w) {
      // eslint-disable-next-line no-alert
      alert('Popup blocked. Please allow popups for this site to print.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();

    // Give browser a short moment to render, then focus and print.
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (err) {
        // ignore printing errors
        // eslint-disable-next-line no-console
        console.warn('Print error', err);
      }
    }, 500);
  };

  /* -----------------------
     Styling for the in-app modal preview
     (mirrors the PDF layout styles and background feel of the admin dashboard)
     ----------------------- */
  const paperStyle = {
    width: 820,
    maxWidth: '100%',
    margin: '24px auto',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 2px 8px rgba(16, 24, 40, 0.06)',
    padding: 22,
    boxSizing: 'border-box',
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
    gap: 12,
    width: '100%',
    flexWrap: 'nowrap',
  };

  const centerTitleStyle = {
    textAlign: 'center',
    flex: 1,
    minWidth: 0,
    margin: '0 18px',
  };

  const centerTitleH5 = {
    marginBottom: 2,
    fontSize: 'clamp(16.5px, 3.75vw, 21px)',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#a82a2e',
  };

  const sectionTitleStyle = {
    background: '#a82a2e',
    color: '#ffff',
    padding: '8px 10px',
    fontWeight: 700,
    marginTop: 14,
    border: '1px solid #e0e9f5',
    borderLeft: '4px solid #474747',
    fontSize: 13,
    borderRadius: 10,
  };

  const chartBoxStyle = {
    height: 260,
    border: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#777',
    marginTop: 8,
    background:
      'linear-gradient(transparent 24px, rgba(229, 231, 235, 0.55) 25px), ' +
      'linear-gradient(90deg, transparent 24px, rgba(229, 231, 235, 0.55) 25px)',
    backgroundSize: '100% 25px, 25px 100%',
  };

  // Ref to access the chart canvas for printing
  const chartContainerRef = React.useRef(null);

  // Derive top product image for on-screen preview (use first item from selectedFan.images)
  const topProductImg = React.useMemo(() => {
    if (!selectedFan) return '';
    let gallery = [];
    if (Array.isArray(selectedFan.images)) {
      gallery = selectedFan.images;
    } else if (typeof selectedFan.images === 'string') {
      try { const parsed = JSON.parse(selectedFan.images); if (Array.isArray(parsed)) gallery = parsed; } catch (_) {}
    }
    if (gallery && gallery.length > 0 && gallery[0]) return toPublicUrl(gallery[0]);
    const fallback = imgSrc(selectedFan);
    if (/^https?:\/\//i.test(fallback) || fallback.startsWith('/')) return fallback;
    return toPublicUrl(fallback);
  }, [selectedFan]);

  const siteLogoSrc = React.useMemo(() => {
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const logoPath = base ? `${base}/logo.png` : '/logo.png';
    return logoPath.replace(/\/{2,}/g, '/');
  }, []);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>Print Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: '#f3f6f8', paddingTop: 12, maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Local styles to normalize TinyMCE HTML inside the modal */}
        <style>{`
          .rich-content { color:#222; }
          .rich-content p { margin:6px 0; }
          .rich-content table { width:100%; border-collapse: collapse; }
          .rich-content th, .rich-content td { border:1px solid #e4e7ea; padding:6px 8px; background:#fff; }
          .rich-content th { background:#fbfdff; font-weight:600; }
        `}</style>
        {selectedFan ? (
          <div style={paperStyle}>
            <div style={{...headerStyle, gap: '12px'}}>
              <div style={{ flex: '0 0 auto' }}>
                <img
                  src={topProductImg || imgSrc(selectedFan)}
                  alt="product"
                  style={{ height: 125, width: 190, objectFit: 'contain', border: 'none', padding: 0, background: 'transparent' }}
                />
              </div>
              <div style={{...centerTitleStyle, flex: '1 1 auto', minWidth: '200px'}}>
                <h5 style={centerTitleH5}>{selectedFan.model_name ?? selectedFan.model ?? selectedFan.name}</h5>
                {selectedFan?.description && (
                  <div style={{ fontSize: 'clamp(11px, 2vw, 13px)', lineHeight: 1.4, color: '#333', marginTop: 4 }}>
                    <div className="rich-content" style={{ fontSize: 'inherit' }} dangerouslySetInnerHTML={{ __html: selectedFan.description }} />
                  </div>
                )}
              </div>
              <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                <img
                  src={siteLogoSrc}
                  alt="Site logo"
                  style={{ height: '120px', maxWidth: '270px', objectFit: 'contain', opacity: 0.95 }}
                />
              </div>
            </div>

            <hr style={{ borderColor: '#eceff2', marginTop: 6, marginBottom: 12 }} />

            <div style={sectionTitleStyle}>Design Point</div>
            <div className="table-responsive">
              <table className="table table-sm" style={{ marginTop: 8 }}>
                <tbody>
                  <tr>
                    <th style={{ width: '40%', background: '#fbfdff' }}>Flow</th>
                    <td>{airFlowUser !== null && airFlowUser !== undefined ? `${Number(airFlowUser).toFixed(2)} ${airFlowUserUnit}` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Pressure</th>
                    <td>{staticPressureUser !== null && staticPressureUser !== undefined ? `${Number(staticPressureUser).toFixed(2)} ${staticPressureUserUnit}` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Ambient Temperature</th>
                    <td>{Number(formData.airTemperature ?? 20).toFixed(2)} °C</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={sectionTitleStyle}>Duty Point</div>
            <div className="table-responsive">
              <table className="table table-sm" style={{ marginTop: 8 }}>
                <tbody>
                  <tr>
                    <th style={{ width: '40%', background: '#fbfdff' }}>Flow</th>
                    <td>{airFlowWorking > 0 ? `${Number(airFlowWorking).toFixed(2)} ${airFlowWorkingUnit}` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Static Pressure</th>
                    <td>{staticPressureWorking > 0 ? `${Number(staticPressureWorking).toFixed(2)} ${staticPressureWorkingUnit}` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Dynamic Pressure</th>
                    <td>{dynamicPressure !== '-' ? `${Number(dynamicPressure).toFixed(2)} ${staticPressureWorkingUnit}` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Total Pressure</th>
                    <td>{totalDynamicPressure !== '-' ? `${Number(totalDynamicPressure).toFixed(2)} ${staticPressureWorkingUnit}` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Diameter</th>
                    <td>{diameter && !Number.isNaN(diameter) && diameter > 0 ? `${Number(diameter).toFixed(2)} mm` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Air Exit Velocity</th>
                    <td>{airExitVelocity !== '-' ? `${Number(airExitVelocity).toFixed(2)} m/s` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Working Point RPM</th>
                    <td>{workingRPM !== null ? `${Number(workingRPM).toFixed(2)} rpm` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Shaft Power</th>
                    <td>{shaftPowerNearest !== null && !Number.isNaN(shaftPowerNearest) ? `${Number(shaftPowerNearest).toFixed(2)} kW` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Specific Fan Power (SPF)</th>
                    <td>{specificFanPower !== null && !Number.isNaN(specificFanPower) ? `${Number(specificFanPower).toFixed(2)} W/(L/s)` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Static Efficiency</th>
                    <td>{staticEfficiency !== '-' ? staticEfficiency : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Total Efficiency</th>
                    <td>{totalEfficiency !== '-' ? totalEfficiency : '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={sectionTitleStyle}>Fan Properties</div>
            <div className="table-responsive">
              <table className="table table-sm" style={{ marginTop: 8 }}>
                <tbody>
                  <tr>
                    <th style={{ width: '40%', background: '#fbfdff' }}>Product Group</th>
                    <td>{selectedFan?.fan_type?.name ?? selectedFan?.fanType?.name ?? '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Diameter</th>
                    <td>{diameter && !Number.isNaN(diameter) && diameter > 0 ? `${Number(diameter).toFixed(2)} mm` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Heat Resistance</th>
                    <td>{selectedFan?.heat_resistance ? `${Number(selectedFan.heat_resistance).toFixed(2)} °C` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Blade Material</th>
                    <td>{selectedFan?.blade_material ?? '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Body Material</th>
                    <td>{selectedFan?.body_material ?? '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Electrical Connection</th>
                    <td>{selectedFan?.electrical_connection ?? '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Fan Drive Mode</th>
                    <td>{selectedFan?.fan_drive_mode ?? '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Max Operating Temperature</th>
                    <td>{selectedFan?.max_operating_temperature ? `${Number(selectedFan.max_operating_temperature).toFixed(2)}` : '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Blade Number / Angle</th>
                    <td>{selectedFan?.blade_number_angle ?? '-'}</td>
                  </tr>
                  <tr>
                    <th style={{ background: '#fbfdff' }}>Blow Position</th>
                    <td>{selectedFan?.blow_position ?? '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Images & Diagrams preview (includes chart and all images) */}
            <div style={{ marginTop: 8 }}>
              <div style={sectionTitleStyle}>Images & Diagrams</div>
              
              {/* Chart as part of Images & Diagrams */}
              <div ref={chartContainerRef} style={{ marginTop: 8 }}>
                {chartData ? (
                  <div style={{ minHeight: 300, maxHeight: '60vh', overflow: 'auto' }}>
                    <PerformanceCharts 
                      chartData={chartData} 
                      powerData={powerData} 
                      onChartClick={() => {}} 
                      userPoint={userPoint}  
                      showPowerChart={showPowerChart}
                      selectedFan={selectedFan}
                      certificates={certificates}
                      motorsForCategory={motorsForCategory}
                      selectedMotorForPrint={selectedMotorForPrint}
                      setSelectedMotorForPrint={setSelectedMotorForPrint}
                      toPublicUrl={toPublicUrl}
                      showTabs={false}
                      airFlowUnit={formData?.airFlowUnit || 'm3h'}
                      pressureUnit={formData?.staticPressureUnit || 'pa'}
                    />
                  </div>
                ) : (
                  <div style={chartBoxStyle}>Performance curve chart (preview)</div>
                )}
              </div>

              {/* Other images */}
              {selectedFan?.sound_power_level_image && (
                <div style={{ marginBottom: 10, marginTop: 8 }}>
                  <div style={sectionTitleStyle}>Sound Power Level</div>
                  <img src={toPublicUrl(selectedFan.sound_power_level_image)} alt="sound" style={{ width: '100%', height: 'auto', border: '1px solid #eee', marginTop: 8 }} />
                </div>
              )}
              {selectedFan?.dimension_image && (
                <div style={{ marginBottom: 10, marginTop: 8 }}>
                  <div style={sectionTitleStyle}>Dimensions</div>
                  <img src={toPublicUrl(selectedFan.dimension_image)} alt="dimension" style={{ width: '100%', height: 'auto', border: '1px solid #eee', marginTop: 8 }} />
                </div>
              )}
              {selectedFan?.connection_diagram_image && (
                <div style={{ marginBottom: 10, marginTop: 8 }}>
                  <div style={sectionTitleStyle}>Connection Diagram</div>
                  <img src={toPublicUrl(selectedFan.connection_diagram_image)} alt="connection" style={{ width: '100%', height: 'auto', border: '1px solid #eee', marginTop: 8 }} />
                </div>
              )}
            </div>

            <div style={sectionTitleStyle}>Motor Information</div>

            {selectedMotorForPrint?.image && (
              <div style={{ marginTop: 8, marginBottom: 8, textAlign: 'center' }}>
                <img 
                  src={toPublicUrl(selectedMotorForPrint.image)} 
                  alt="Motor Image" 
                  style={{ maxWidth: 300, maxHeight: 300, objectFit: 'contain', border: '1px solid #eee' }} 
                  onError={(e) => {
                    console.warn('Failed to load motor image:', selectedMotorForPrint.image);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}


            {selectedMotorForPrint ? (
              <div className="table-responsive">
                <table className="table table-sm" style={{ marginTop: 10 }}>
                  <tbody>
                    <tr>
                      <th style={{ width: '40%', background: '#fbfdff' }}>Nominal Motor Power</th>
                      <td>{selectedMotorForPrint.nominal_motor_power ?? selectedMotorForPrint.nominalMotorPower ?? '-'} kW</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>Nominal Fan Speed</th>
                      <td>{selectedMotorForPrint.nominal_fan_speed ?? selectedMotorForPrint.nominalFanSpeed ?? selectedMotorForPrint.rpm ?? '-'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>No. of Poles</th>
                      <td>{selectedMotorForPrint.no_of_poles ?? selectedMotorForPrint.noOfPoles ?? '-'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>Nominal Motor Current</th>
                      <td>{selectedMotorForPrint.nominal_motor_current ?? selectedMotorForPrint.nominalMotorCurrent ?? '-'} A</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>Voltage</th>
                      <td>{selectedMotorForPrint.voltage ?? selectedMotorForPrint.voltage_v ?? '-'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>Frequency</th>
                      <td>{selectedMotorForPrint.frequency ?? selectedMotorForPrint.freq ?? '-'} Hz</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>Phase</th>
                      <td>{selectedMotorForPrint.phase ?? selectedMotorForPrint.phases ?? '-'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>İnsulation class</th>
                      <td>{selectedMotorForPrint.insulation_class ?? selectedMotorForPrint.insulationClass ?? '-'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>Efficiency</th>
                      <td>{selectedMotorForPrint.efficiency ?? selectedMotorForPrint.eff ?? '-'} %</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#fbfdff' }}>Working Class</th>
                      <td>{selectedMotorForPrint.working_class ?? selectedMotorForPrint.workingClass ?? '-'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info" style={{ marginTop: 10 }}>No suitable motor found for this fan category and required power.</div>
            )}

            {/* Certificates row */}
            {Array.isArray(certificates) && certificates.length > 0 && (
              <>
                <div style={sectionTitleStyle}>Certificates</div>
                <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, paddingTop: 8, paddingBottom: 8, overflow: 'visible' }}>
                  {certificates.map((c, i) => (
                    c?.image ? (
                      <img 
                        key={i} 
                        src={c.image} 
                        alt={c.name || 'certificate'} 
                        style={{ 
                          maxHeight: 200, 
                          maxWidth: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block'
                        }} 
                      />
                    ) : null
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div>No fan selected</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onHide}>Close</button>
        <button type="button" className="btn btn-primary" onClick={printSelectedFan}>Print / Open PDF</button>
      </Modal.Footer>
    </Modal>
  );
}

PrintPreviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedFan: PropTypes.object,
  formData: PropTypes.object,
  nearestPoint: PropTypes.object,
  motorsForCategory: PropTypes.array,
  selectedMotorForPrint: PropTypes.object,
  setSelectedMotorForPrint: PropTypes.func,
  chartData: PropTypes.object,
  powerData: PropTypes.object,
  userPoint: PropTypes.object,
  showPowerChart: PropTypes.bool,
};

PrintPreviewModal.defaultProps = {
  selectedFan: null,
  formData: {},
  nearestPoint: null,
  motorsForCategory: [],
  selectedMotorForPrint: null,
  setSelectedMotorForPrint: () => {},
  chartData: null,
  powerData: null,
  userPoint: null,
  showPowerChart: true,
};
